// Runtime configuration for the CLMS SPA.
//
// DoD-6/7: nothing environment-specific is hardcoded. Values come from Vite env
// (build-time, `VITE_*`) with an optional runtime override injected as
// `window.__CLMS_CONFIG__` by the hosting page — so the same built bundle can be
// deployed against different backends/tenants without a rebuild.

export interface ClmsConfig {
  /** Base URL of the ASP.NET Core API. Empty string => same-origin (dev proxy). */
  apiBaseUrl: string;
  /** OIDC issuer (Keycloak realm). Auth screens live on Keycloak, not here. */
  oidcIssuer: string;
  /** OIDC client id for this SPA. */
  oidcClientId: string;
  /**
   * The Keycloak client whose CLIENT roles are this application's role codes (ADR-0014 §4.1): the backend
   * API's own client. Not this SPA's client — authority belongs to the API that enforces it, so the token
   * carries the codes under `resource_access[apiClientId]` whichever front end obtained it.
   */
  apiClientId: string;
  /**
   * The operator console's client, which carries `PLATFORM_ADMIN` (ADR-0014 §4.1/§6). Read in addition to
   * `apiClientId` because the person signs into the product SPA as well, and the nav has to know.
   */
  platformClientId: string;
}

declare global {
  interface Window {
    __CLMS_CONFIG__?: Partial<ClmsConfig>;
  }
}

/**
 * The OIDC client the ENTRY being built authenticates with when no `VITE_OIDC_CLIENT_ID` is
 * configured. Each entry's vite config `define`s it — `clms-spa` for the product SPA (apps/web),
 * `clms-platform` for the operator console (apps/platform).
 *
 * It is a property of the entry rather than of a deployment, which is why it is a build-time constant
 * and not a default spelled here: ADR-0014 §6 phase 2 gave the platform surface its own client
 * precisely so the C-2 guard can say "the call arrived on the platform client AND the caller holds
 * platform authority", and a console that fell back to the product's client would make that check
 * unsatisfiable while still appearing to work. A deployment that must name a different client still
 * overrides both with the env variable below.
 */
declare const __CLMS_ENTRY_OIDC_CLIENT_ID__: string;

const fromEnv: ClmsConfig = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? "",
  oidcIssuer: import.meta.env.VITE_OIDC_ISSUER ?? "",
  oidcClientId: import.meta.env.VITE_OIDC_CLIENT_ID ?? __CLMS_ENTRY_OIDC_CLIENT_ID__,
  // Both entries read the same two role carriers, so these are plain defaults rather than per-entry
  // constants: the carrier is a property of the realm, not of which bundle is running.
  apiClientId: import.meta.env.VITE_API_CLIENT_ID ?? "clms-api",
  platformClientId: import.meta.env.VITE_PLATFORM_CLIENT_ID ?? "clms-platform",
};

const runtime = typeof window !== "undefined" ? window.__CLMS_CONFIG__ : undefined;

export const config: ClmsConfig = { ...fromEnv, ...runtime };

/**
 * The tenant the URL names BY PATH (`/t/<slug>/…`), or null. Split out of
 * {@link resolveTenantSlug} so that {@link tenantPath} answers from the same rule rather than a second
 * copy of this expression — the whole lesson of task e0ff5429 below is what a second copy of a tenant
 * rule costs.
 */
function pathTenantSlug(loc: Location): string | null {
  const match = loc.pathname.match(/^\/t\/([a-z0-9-]+)(?:\/|$)/i);
  return match ? match[1].toLowerCase() : null;
}

/**
 * `path` addressed at the tenant the CURRENT url names by path — so a hop between the PRE-SESSION pages
 * keeps the one thing that tells the backend which organisation the person is signing in to.
 *
 * Returned unchanged when neither the path nor `fallbackSlug` names a tenant, which is right in both of
 * the cases that reach it: on a per-tenant subdomain the HOST carries the tenant, and on the bare apex
 * there is no tenant to carry — the target page then resolves none and says so, which is the honest answer
 * rather than a guess.
 *
 * Why it is needed at all: the authenticated shell is apex-only and the `/t/:slug/` routes are pre-session
 * (src/app/router.tsx), so every link BETWEEN the three sign-in pages — "pyydä uusi linkki" on a dead
 * redeem, "takaisin kirjautumiseen" on the request page — used to drop the prefix and land the person on
 * "Organisaatiota ei löytynyt" on a deployment whose host names no tenant (task f390743e). The fourth
 * pre-session route, the mailed questionnaire link's landing, feeds the same chain: it hands an anonymous
 * arrival straight to that tenant's identity provider and offers `/t/<slug>/login/email` as the way out
 * for a learner who has no organisation account (task f71bf1eb, then 09e495b1 —
 * src/app/TenantRespondEntry.tsx).
 *
 * `fallbackSlug` is for the ONE caller whose page has already left the `/t/<slug>/` prefix behind and is
 * not a sign-in page at all: `RequireAuth`'s bounce out of the apex shell (src/app/RequireAuth.tsx, task
 * 09e495b1). It is read only when the path names nothing, so an address that DOES name a tenant still
 * wins — that order is the same one `lib/api`'s `tenantHint` applies, and for the same reason.
 */
export function tenantPath(
  path: string,
  loc: Location = window.location,
  fallbackSlug: string | null = null,
): string {
  const slug = pathTenantSlug(loc) ?? fallbackSlug;
  return slug ? `/t/${encodeURIComponent(slug)}${path}` : path;
}

/**
 * Resolve the current tenant from the host/path (ADR-0001: tenant is identified
 * by subdomain or path). Returns null when no tenant is addressable (e.g. the
 * bare apex host in local dev), letting the UI show the "unknown organisation"
 * state instead of guessing.
 */
export function resolveTenantSlug(loc: Location = window.location): string | null {
  // Path form: /t/<slug>/... takes precedence (works on localhost).
  const fromPath = pathTenantSlug(loc);
  if (fromPath) return fromPath;

  // Subdomain form: <slug>.clms.example — ignore common non-tenant hosts.
  //
  // `platform` is reserved because ADR-0014 §6 phase 2 gave the operator console its own host
  // (`platform.<domain>`, apps/platform). Without it that host would derive the tenant slug
  // "platform" from its own first label, and the cross-tenant console would send
  // `X-Tenant: platform` on every call and look up branding for an organisation that does not
  // exist. The console addresses no tenant: it is the one surface that spans them all.
  //
  // The list below is the SAME list the API reserves, and it has to be: the backend derives the
  // same hint off the Host header when no `X-Tenant` is sent, and a label reserved on only one
  // side is a 403 on every call from that host (task e0ff5429 — `platform.versopeople.com` and
  // the apex in production, v0.1.3). The API's copy is `TenantHostLabels` in Clms.Domain, read by
  // `TenantHint`; `auth` joined both lists there, since Keycloak's public host is `auth.<domain>`.
  const host = loc.hostname;
  const labels = host.split(".");
  const reserved = new Set(["www", "app", "platform", "auth", "localhost", "127", "0"]);
  if (labels.length >= 3 && !reserved.has(labels[0])) {
    return labels[0].toLowerCase();
  }
  return null;
}
